import {useMemo, useState} from "react"
import {Input, Tag} from "antd"
import cx from "classnames"
import moment from "moment-timezone"
import {useMutation, useQueryClient} from "@tanstack/react-query"
import {Permissions} from "@edular/permissions"
import {BaseButton} from "components/buttons"
import {BasePopup} from "components/popup"
import {handleError, toastSuccess} from "helpers"
import {useCurrentProfilePermissions, useModel} from "hooks"
import {attendanceService} from "services"
import {
    AttendanceChangeRequest,
    AttendanceChangeRequestStatus,
    AttendanceEventSnapshot,
    AttendanceEventType
} from "types/attendance"
import {buildDiffRows, ChangeDiffRow, getBaseEvents} from "utils/attendanceChangeRequest"
import styles from "./DailyAttendanceChangeRequests.module.css"

export const CHANGE_REQUEST_STATUS_COLOR = {
    [AttendanceChangeRequestStatus.Pending]: "gold",
    [AttendanceChangeRequestStatus.Approved]: "green",
    [AttendanceChangeRequestStatus.Rejected]: "red",
    [AttendanceChangeRequestStatus.Cancelled]: "default"
}

const EVENT_LABELS: Record<string, string> = {
    [AttendanceEventType.Checkin]: "Check-in",
    [AttendanceEventType.Checkout]: "Check-out",
    [AttendanceEventType.BreakStart]: "Out",
    [AttendanceEventType.BreakEnd]: "In"
}

type Props = {
    request?: AttendanceChangeRequest
    timezone: string
    onClose: () => void
    onViewAttendance?: (request: AttendanceChangeRequest) => void
    onReviewed?: () => void
}

export function ChangeRequestReviewPopup(props: Props) {
    const {request, timezone, onClose, onViewAttendance, onReviewed} = props
    const model = useModel()
    const queryClient = useQueryClient()
    const permissions = useCurrentProfilePermissions()
    const [rejecting, setRejecting] = useState(false)
    const [note, setNote] = useState("")

    const canEdit = permissions.hasPermissions({
        staff: [Permissions.Staff.AcademicAffairs.StudentServices.Attendance.Edit]
    })
    const canOverrideLocked = permissions.hasPermissions({
        staff: [Permissions.Staff.Academics.Registrar.Attendance.LockedAttendance.Edit]
    })
    const canReview =
        request?.status === AttendanceChangeRequestStatus.Pending &&
        !request.attendanceDeletedAt &&
        (request.attendanceIsLocked ? canOverrideLocked : canEdit)

    const rows = useMemo(() => (request ? buildDiffRows(getBaseEvents(request), request.changes) : []), [request])

    const renderEvent = (event?: Pick<AttendanceEventSnapshot, "eventType" | "createdAt">) =>
        event ? (
            <>
                <span className={styles.eventLabel}>{EVENT_LABELS[event.eventType] ?? event.eventType}</span>
                <span className={styles.eventTime}>
                    {moment(event.createdAt).tz(timezone).format(model.getUserTimeFormat())}
                </span>
            </>
        ) : (
            <span className={styles.muted}>-</span>
        )

    const close = () => {
        setRejecting(false)
        setNote("")
        onClose()
    }

    const reviewMutation = useMutation(attendanceService.reviewAttendanceChangeRequest, {
        onSuccess: ({skippedEventIds}) => {
            toastSuccess(
                skippedEventIds.length
                    ? `Reviewed. ${skippedEventIds.length} change(s) skipped: event no longer exists`
                    : "Reviewed successfully"
            )
            queryClient.invalidateQueries(["attendance-change-requests"])
            queryClient.invalidateQueries(["load-attendances"])
            onReviewed?.()
            close()
        },
        onError: (err) => {
            handleError(err, true)
        }
    })

    const renderCurrent = (row: ChangeDiffRow) => {
        switch (row.kind) {
            case "unchanged":
            case "removed":
                return renderEvent(row.event)
            case "edited":
                return renderEvent(row.before)
            case "missing":
                return renderEvent(row.event)
            default:
                return <span className={styles.muted}>-</span>
        }
    }

    const renderRequested = (row: ChangeDiffRow) => {
        switch (row.kind) {
            case "unchanged":
                return <span className={styles.muted}>No change</span>
            case "edited":
                return renderEvent(row.after)
            case "added":
                return renderEvent(row.event)
            case "removed":
                return <span className={styles.removedText}>Removed</span>
            case "missing":
                return <span className={styles.muted}>Event no longer exists</span>
        }
    }

    const reviewerTitle =
        request?.status === AttendanceChangeRequestStatus.Approved
            ? "Approved by"
            : request?.status === AttendanceChangeRequestStatus.Rejected
            ? "Rejected by"
            : "Reviewed by"

    return (
        <BasePopup isShow={!!request} onClose={close} leftIcon="PERSON_FILL" width="60vw" destroyOnClose>
            {request && (
                <div className={styles.popupWrapper}>
                    <p className={styles.title}>Change request</p>
                    <div className={styles.divisor} />
                    <div className={styles.summary}>
                        <div className={styles.metaItem}>
                            <span className={styles.metaLabel}>Student</span>
                            <span className={styles.metaValue}>{request.studentName}</span>
                        </div>
                        <div className={styles.metaItem}>
                            <span className={styles.metaLabel}>Attendance date</span>
                            <span className={styles.metaValue}>
                                {moment(request.attendanceDate).format(model.getUserDateFormat())}
                            </span>
                        </div>
                        <div className={styles.metaItem}>
                            <span className={styles.metaLabel}>Requested at</span>
                            <span className={styles.metaValue}>
                                {moment(request.createdAt).format(model.getUserDateTimeFormat())}
                            </span>
                        </div>
                        <div className={styles.metaItem}>
                            <span className={styles.metaLabel}>Status</span>
                            <span>
                                <Tag color={CHANGE_REQUEST_STATUS_COLOR[request.status]}>{request.status}</Tag>
                            </span>
                        </div>
                    </div>
                    <div className={styles.block}>
                        <span className={styles.blockTitle}>Reason</span>
                        <p className={styles.reason}>{request.reason}</p>
                    </div>
                    <div className={styles.block}>
                        <span className={styles.blockTitle}>Requested changes</span>
                        <div className={styles.diff}>
                            <div className={cx(styles.diffRow, styles.diffHead)}>
                                <span>Current</span>
                                <span />
                                <span>Requested</span>
                            </div>
                            {rows.map((row, index) => (
                                <div key={index} className={cx(styles.diffRow, styles[`row-${row.kind}`])}>
                                    <div className={styles.cell}>{renderCurrent(row)}</div>
                                    <span className={styles.arrow}>→</span>
                                    <div className={cx(styles.cell, styles.requestedCell)}>{renderRequested(row)}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                    {request.status !== AttendanceChangeRequestStatus.Pending && !!request.reviewedAt && (
                        <div className={cx(styles.outcome, styles[`outcome-${request.status}`])}>
                            <span className={styles.metaLabel}>{reviewerTitle}</span>
                            <span className={styles.metaValue}>
                                {request.reviewedByName ?? "-"} ·{" "}
                                {moment(request.reviewedAt).format(model.getUserDateTimeFormat())}
                            </span>
                            {!!request.reviewNote && <p className={styles.reason}>{request.reviewNote}</p>}
                        </div>
                    )}
                    {canReview && rejecting && (
                        <Input.TextArea
                            rows={3}
                            placeholder="Reason for rejecting (required)"
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                        />
                    )}
                    {(canReview || onViewAttendance) && (
                        <div className={styles.actions}>
                            {onViewAttendance && !rejecting && (
                                <BaseButton
                                    title="View attendance"
                                    variant="secondary"
                                    onClick={() => onViewAttendance(request)}
                                />
                            )}
                            {!canReview ? null : rejecting ? (
                                <>
                                    <BaseButton title="Back" variant="secondary" onClick={() => setRejecting(false)} />
                                    <BaseButton
                                        title="Confirm reject"
                                        danger
                                        disabled={!note.trim()}
                                        loading={reviewMutation.isLoading}
                                        onClick={() =>
                                            reviewMutation.mutate({
                                                id: request.id,
                                                decision: "reject",
                                                note: note.trim()
                                            })
                                        }
                                    />
                                </>
                            ) : (
                                <>
                                    <BaseButton
                                        title="Reject"
                                        variant="secondary"
                                        danger
                                        onClick={() => setRejecting(true)}
                                    />
                                    <BaseButton
                                        title="Approve"
                                        loading={reviewMutation.isLoading}
                                        onClick={() => reviewMutation.mutate({id: request.id, decision: "approve"})}
                                    />
                                </>
                            )}
                        </div>
                    )}
                </div>
            )}
        </BasePopup>
    )
}
